import { createUserWithEmailAndPassword, signOut } from '../../auth/staffAuthApi';
import { auth } from '../../auth/appAuth';
import { sendCustomVerificationEmail } from '../../auth/api/emailVerificationApi';
import { getAuthorizeEnvironment } from './payment/authorizeEnvironment';
import { INITIAL_TUTORIAL_STATE } from '../../components/tutorial/constants/initialTutorialState';
import { DEFAULT_BOOKING_CHECK_IN_SETTINGS } from '../../components/settings/pages/booking/defaultBookingCheckInSettings';
import { provisionPaidAccount } from './accountSetup/provisionPaidAccount';
import { featureFlags } from '../../config/featureFlags';
import { provisionAccount } from '../../api/signup';
import { listPackages } from '../../api/subscriptions/subscriptions';

// Package name mapping
const PACKAGE_NAMES = {
  'solo': 'Solo',
  'business': 'Business Package',
  'business_plus': 'Business Plus',
  'custom': 'Custom',
  'enterprise': 'Custom',
  'free_trial': 'Free Trial'
};

const getFallbackEmployeeLimit = (packageId) => {
  if (packageId === 'enterprise') return -1;
  if (packageId === 'business_plus') return 25;
  if (packageId === 'solo') return 1;
  return 5;
};

const getFallbackMonthlyPrice = (packageId) => {
  if (packageId === 'enterprise') return 500;
  if (packageId === 'business_plus') return 80;
  if (packageId === 'solo') return 20;
  return 40;
};

const getFallbackYearlyPrice = (packageId) => {
  if (packageId === 'enterprise') return 4800;
  if (packageId === 'business_plus') return 768;
  if (packageId === 'solo') return 192;
  return 384;
};

// Default package features and subscription settings
const getPackageDefaults = async (packageId, billingCycle = 'monthly') => {
  const now = new Date().toISOString();
  const endDate = new Date();
  
  // Set end date based on billing cycle or trial period
  if (packageId === 'free_trial') {
    endDate.setDate(endDate.getDate() + 14); // 14-day trial
  } else if (billingCycle === 'yearly') {
    endDate.setFullYear(endDate.getFullYear() + 1);
  } else {
    endDate.setMonth(endDate.getMonth() + 1);
  }

  // Fetch package data from Firestore to get current features
  let packageFeatures = [];
  let packageLimits = {
    employees: getFallbackEmployeeLimit(packageId),
    appointments: -1
  };
  let subscriptionSettings = {
    gracePeriodDays: 7,
    trialDays: 0,
    autoRenew: true
  };

  try {
    // Try to fetch the package from the packages collection
    const packages = await listPackages();
    
    // Find the matching package by name or ID
    let matchedPackage = null;
    
    if (packageId === 'free_trial') {
      matchedPackage = packages.find((pkg) => pkg.isTrialPackage === true)
        || packages.find(pkg =>
          pkg.name?.toLowerCase().includes('trial')
        );
    } else {
      // Map old package IDs to new package names
      const packageNameMapping = {
        'solo': 'Solo',
        'business': 'Business',
        'business_plus': 'Business Plus',
        'custom': 'Custom',
        'enterprise': 'Custom',
      };

      const targetName = packageNameMapping[packageId];
      matchedPackage = packages.find(pkg =>
        pkg.slug === packageId ||
        pkg.name === targetName ||
        pkg.id === packageId
      );
    }

    if (matchedPackage) {
      packageFeatures = matchedPackage.features || [];
      packageLimits = matchedPackage.limits || packageLimits;
      subscriptionSettings = {
        gracePeriodDays: matchedPackage.subscription?.gracePeriodDays || 7,
        trialDays: matchedPackage.subscription?.trialDays || (packageId === 'free_trial' ? 14 : 0),
        autoRenew: matchedPackage.subscription?.autoRenew !== undefined ? matchedPackage.subscription.autoRenew : true
      };
    } else {
      console.warn(`Package not found in database: ${packageId}, using fallback features`);
      // Fallback to basic features if package not found
      packageFeatures = packageId === 'enterprise' ? 
        ['Priority Support', 'Advanced Analytics', 'Custom Integrations'] :
        packageId === 'business_plus' ?
        ['Multi-Calendar Support', 'Advanced Scheduling'] :
        ['Basic Scheduling', 'Email Notifications'];
    }
  } catch (error) {
    console.error('Error fetching package data:', error);
    // Fallback to basic features on error
    packageFeatures = ['Basic Features'];
  }

  return {
    features: packageFeatures,
    limits: packageLimits,
    subscription: {
      status: packageId === 'free_trial' ? 'trial' : 'active',
      gracePeriodDays: subscriptionSettings.gracePeriodDays,
      trialDays: subscriptionSettings.trialDays,
      autoRenew: subscriptionSettings.autoRenew,
      billingCycle: billingCycle,
      currentPrice: packageId === 'free_trial' ? 0 :
        billingCycle === 'yearly' ?
          getFallbackYearlyPrice(packageId) :
          getFallbackMonthlyPrice(packageId),
      startDate: now,
      endDate: endDate.toISOString(),
      nextBillingDate: endDate.toISOString()
    }
  };
};

export const createAccount = async (setupData) => {
  try {
    console.log('Creating account with setup data:', setupData);

    // Validate subscription data
    if (!setupData.subscription || !setupData.subscription.id || !setupData.package) {
      console.error('Missing required setup data:', {
        subscription: setupData.subscription,
        package: setupData.package
      });
      throw new Error('Missing required setup data: subscription information or package type');
    }

    if (setupData.package !== 'free_trial') {
      return provisionPaidAccount(setupData);
    }

    if (!featureFlags.FREE_TRIAL_ENABLED) {
      throw new Error('Payment was not confirmed. No account was created.');
    }

    // Create the staff account
    const userCredential = await createUserWithEmailAndPassword(
      auth, 
      setupData.businessInfo.email, 
      setupData.businessInfo.password
    );
    const user = userCredential.user;
    const now = new Date().toISOString();

    // Send custom verification email (SendGrid, no-reply@neatschedule.com)
    try {
      const idToken = await user.getIdToken();
      await sendCustomVerificationEmail(idToken);
    } catch (verifyErr) {
      console.warn('Could not send verification email:', verifyErr);
    }

    const companyId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `company_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    // Get the proper package name and defaults
    const packageName = setupData.packageName || PACKAGE_NAMES[setupData.package] || 'Business Package';
    
    console.log('Package name resolution:', {
      providedPackageName: setupData.packageName,
      packageId: setupData.package,
      mappedPackageName: PACKAGE_NAMES[setupData.package],
      finalPackageName: packageName
    });
    const packageDefaults = await getPackageDefaults(setupData.package, setupData.billingCycle);

    let packageConfig = packageDefaults;
    if (setupData.package === 'free_trial') {
      try {
        const trialPackage = (await listPackages()).find(
          (pkg) => pkg.isTrialPackage === true && pkg.isActive
        );

        if (trialPackage) {
          packageConfig = {
            features: trialPackage.features || packageDefaults.features,
            limits: trialPackage.limits || packageDefaults.limits,
            subscription: {
              ...packageDefaults.subscription,
              gracePeriodDays: trialPackage.subscription?.gracePeriodDays || 0,
              trialDays: trialPackage.subscription?.trialDays || 14,
            },
            firestorePackageId: trialPackage.id,
          };
        }
      } catch (error) {
        console.warn('Error fetching trial package config:', error);
      }
    }

    // Ensure valid dates for subscription
    const safeDate = (dateString) => {
      try {
        if (!dateString) return now;
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? now : date.toISOString();
      } catch (e) {
        console.warn('Invalid date conversion:', e);
        return now;
      }
    };

    console.log('Package details:', {
      name: packageName,
      defaults: packageConfig,
      subscription: setupData.subscription
    });

    // Prepare company data with subscription info
    const companyData = {
      name: setupData.businessInfo.companyName,
      createdAt: now,
      updatedAt: now,
      ownerId: user.uid,
      ownerFirstName: setupData.businessInfo.firstName,
      ownerLastName: setupData.businessInfo.lastName,
      // Business phone (required)
      businessPhone: setupData.businessInfo.businessPhoneE164 || setupData.businessInfo.businessPhone,
      businessPhoneFormatted: setupData.businessInfo.businessPhone,
      // Personal phone (optional)
      ...(setupData.businessInfo.personalPhone && {
        personalPhone: setupData.businessInfo.personalPhoneE164 || setupData.businessInfo.personalPhone,
        personalPhoneFormatted: setupData.businessInfo.personalPhone
      }),
      email: setupData.businessInfo.email,
      settings: {
        depositSettings: {
          enabled: false,
          amount: 0,
          percentage: 0,
          type: 'fixed'
        },
        bookingSettings: DEFAULT_BOOKING_CHECK_IN_SETTINGS,
        tutorial: INITIAL_TUTORIAL_STATE,
      },
      themeColor: '#1976d2',
      themeMode: 'light',
      package: {
        name: packageName,
        id: setupData.package,
        limits: setupData.packageDetails?.limits || packageConfig.limits || {
          employees: getFallbackEmployeeLimit(setupData.package),
          appointments: -1
        },
        features: packageConfig.features,
        subscription: {
          id: setupData.subscription.id,
          status: setupData.subscription.status,
          billingCycle: setupData.subscription.billingCycle,
          amount: setupData.subscription.amount,
          startDate: safeDate(setupData.subscription.startDate),
          nextBillingDate: safeDate(setupData.subscription.nextBillingDate),
          autoRenew: true,
          currentPrice: setupData.subscription.amount,
          gracePeriodDays: packageConfig.subscription.gracePeriodDays,
          trialDays: packageConfig.subscription.trialDays,
          updatedAt: now,
          customerProfileId: setupData.subscription.customerProfileId || null,
          customerPaymentProfileId: setupData.subscription.customerPaymentProfileId || null,
          paymentMethodLast4:
            setupData.subscription.paymentMethodLast4
            || setupData.subscription.paymentProfile?.last4
            || null,
          authorizeEnvironment: getAuthorizeEnvironment(),
        },
        price: {
          monthly: packageConfig.subscription.currentPrice,
          annually: packageConfig.subscription.currentPrice * 12 * 0.8 // 20% annual discount
        },
        createdAt: now,
        updatedAt: now
      },
      resourceCounts: {
        employees: 1,
        appointments: 0
      },
      employeesCount: 1,
      appointmentsCount: 0
    };

    console.log('Creating company with data:', companyData);

    const userData = {
      email: setupData.businessInfo.email,
      firstName: setupData.businessInfo.firstName,
      lastName: setupData.businessInfo.lastName,
      // Business phone (required)
      businessPhone: setupData.businessInfo.businessPhoneE164 || setupData.businessInfo.businessPhone,
      businessPhoneFormatted: setupData.businessInfo.businessPhone,
      // Personal phone (optional)
      ...(setupData.businessInfo.personalPhone && {
        personalPhone: setupData.businessInfo.personalPhoneE164 || setupData.businessInfo.personalPhone,
        personalPhoneFormatted: setupData.businessInfo.personalPhone
      }),
      companyId,
      companyName: setupData.businessInfo.companyName,
      role: 'owner',
      accountType: 'company', // Distinguish from client portal accounts
      emailVerified: false,
      createdAt: now,
      updatedAt: now,
      lastLogin: now,
      status: 'active',
      permissions: {
        CALENDAR: {
          VIEW: true,
          CREATE: true,
          EDIT: true,
          DELETE: true
        },
        APPOINTMENTS: {
          VIEW: true,
          CREATE: true,
          EDIT: true,
          DELETE: true
        },
        CLIENTS: {
          VIEW: true,
          CREATE: true,
          EDIT: true,
          DELETE: true
        },
        ARCHIVES: { VIEW: true },
        SETTINGS: {
          VIEW: true,
          EDIT: true
        }
      }
    };

    const idToken = await user.getIdToken();
    await provisionAccount({
      token: idToken,
      companyId,
      company: { id: companyId, ...companyData },
      user: userData,
    });

    // Sign out so the user lands on the success/login flow instead of the app shell
    // while email verification is still pending.
    try {
      await signOut(auth);
    } catch (signOutError) {
      console.warn('Could not sign out after account creation:', signOutError);
    }

    return {
      success: true,
      accountId: user.uid,
      message: 'Account created successfully'
    };

  } catch (error) {
    console.error("Registration error:", error);
    throw error;
  }
}; 